import { isPricingName } from '@client/utils/validate';
import { addIdAndTypeToFilters } from '@components/sort-and-filter/utils';
import { FEATURE_FLAGS } from '@feature-flags/constants';
import type { FeatureFlags } from '@feature-flags/types';
import {
	CATEGORY_FILTER_ID,
	type FilterConfig,
	type FilterConfigs,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import {
	outdoorFilter,
	settingsFilter,
} from '@pages/VendorsSearch/containers/FilterPills/helpers';
import type { FilterDetails, SearchFilters } from 'types/search';
import type { CategorySummary } from '../types';

const EXCLUDED_CATEGORY_CODES: ReadonlySet<string> = new Set(['HRB']);
const CONFIDENCE_AND_SUPPORT_FILTER_SLUG = 'confidence-and-support';
const PEACE_OF_MIND_FILTER_OPTION_ID = '19c8944d-4220-4b1d-950c-a33f95e63833';

export const buildCategoryFilterConfig = (
	allCategories: CategorySummary[],
): FilterConfig => ({
	id: CATEGORY_FILTER_ID,
	type: 'facet-multi-select',
	name: 'Category',
	filterOptions: allCategories
		.filter((c) => !EXCLUDED_CATEGORY_CODES.has(c.code))
		.map((c) => ({
			id: c.id,
			name: c.name,
			code: c.code,
			singular: { slug: c.seo.singular.slug, term: c.seo.singular.term },
			plural: { slug: c.seo.plural.slug, term: c.seo.plural.term },
		})),
	slug: 'category',
});

const CONFIDENCE_AND_SUPPORT_FILTER: FilterDetails = {
	id: 'confidence-and-support',
	type: 'facet-multi-select',
	name: 'Confidence & support',
	slug: 'confidence-and-support',
	isSingleSelect: false,
	filterOptions: [
		{
			id: PEACE_OF_MIND_FILTER_OPTION_ID,
			name: 'Peace of Mind',
			plural: {
				slug: 'peace-of-mind',
				term: 'Peace of Mind',
			},
			singular: {
				slug: 'peace-of-mind',
				term: 'Peace of Mind',
			},
		},
	],
};

const getPeaceOfMindFilterFromBackend = (
	filters: SearchFilters,
): FilterDetails | undefined => {
	return filters.filters.find(
		(filter) => filter.slug === CONFIDENCE_AND_SUPPORT_FILTER_SLUG,
	);
};

const removeOutdoorOption = (filter: FilterDetails): FilterDetails => {
	const { filterOptions, name } = filter;
	if (name === 'Outdoor Space') return filter;
	const filteredOptions = filterOptions.filter(
		(option) =>
			option.name !== 'Outdoor Event Space' &&
			option.name !== 'Covered Outdoors Space',
	);
	return { ...filter, filterOptions: filteredOptions };
};

const modifySettingsFilter = (filter: FilterDetails): FilterDetails => {
	if (filter.name !== 'Settings') return filter;
	const modifiedFilterOptions = filter.filterOptions.filter((option) => {
		return !settingsFilter.filterOptions.some(
			(newFilters) => newFilters.id === option.id,
		);
	});
	return {
		...filter,
		filterOptions: [...modifiedFilterOptions, ...settingsFilter.filterOptions],
	};
};

const isAffiliationsFilter = (filter: FilterDetails, categorySlug: string) => {
	if (categorySlug !== 'wedding-reception-venues') {
		return false;
	}
	return filter.slug === 'affiliations';
};

const modifyPriceFilter = (
	filter: FilterDetails,
	featureFlags: FeatureFlags,
): FilterDetails => {
	const { name, slug } = filter;
	if (featureFlags[FEATURE_FLAGS.LISTINGS_PRICING] && isPricingName(name)) {
		return {
			id: 'starting-price',
			type: 'pricing',
			name,
			slug,
			filterOptions: [],
		} as unknown as FilterDetails;
	}
	return filter;
};
/**
 * Filter IDs whose single-select dropdown should NOT include the synthetic 'All' option.
 * Add a filter's `id` (its slug, e.g. 'price') to this set to opt it out.
 */
const FILTERS_WITHOUT_ALL_OPTION: ReadonlySet<string> = new Set<string>([
	'deals',
]);

export const buildFilterConfigs = (
	filters: SearchFilters,
	featureFlags: FeatureFlags,
	categorySlug: string,
	shouldAddPeaceOfMindFilter = false,
) => {
	const peaceOfMindFilterFromBackend = getPeaceOfMindFilterFromBackend(filters);

	const filterList = addIdAndTypeToFilters([
		...filters.filters,
		...(categorySlug ? [outdoorFilter as FilterDetails] : []),
		...(peaceOfMindFilterFromBackend
			? [peaceOfMindFilterFromBackend]
			: shouldAddPeaceOfMindFilter
				? [CONFIDENCE_AND_SUPPORT_FILTER]
				: []),
	]);
	return filterList.reduce((acc, filter) => {
		if (isAffiliationsFilter(filter, categorySlug)) {
			return acc;
		}
		const updatedFilter = modifyPriceFilter(
			modifySettingsFilter(removeOutdoorOption(filter)),
			featureFlags,
		);
		acc[updatedFilter.id] = FILTERS_WITHOUT_ALL_OPTION.has(updatedFilter.id)
			? { ...updatedFilter, hideAllOption: true }
			: updatedFilter;
		return acc;
	}, {} as FilterConfigs);
};
